import { useRoutes } from '@folklore/routes';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { Suspense, useEffect } from 'react';
import { useParams, useRoute } from 'wouter';

import { useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';

import { useDefaultBrand } from '../../contexts/SiteContext';
import Survey from '../../interactives/survey';
import DocumentTheme from '../themes/DocumentTheme';

import styles from '../../styles/documents/survey-document.module.css';

const SurveyComponents = {
    Default: Survey,
};

interface SurveyDocumentProps extends Partial<SurveyDocument> {
    component?: keyof typeof SurveyComponents;
    placeholder?: boolean;
    className: 'string';
    current?: boolean;
    onEnter?: () => void;
    onLeave?: () => void;
}

function SurveyDocument({
    id = null,
    type = 'survey',
    slug = null,
    title = null,
    blocks = null,
    theme = null,
    subscriptions = null,
    metadata = null,
    form = null,
    image = null,
    images = null,
    component = null,
    placeholder = false,
    className = null,
    current = false,
    onEnter = null,
    onLeave = null,
}: SurveyDocumentProps) {
    const SurveyComponent = getComponentFromName(SurveyComponents, component, 'default');
    const { header: headerImage = null } = images || {};
    const { brands } = metadata || {};
    const { name: themeName = null, fullscreen = false } = theme || {};
    const defaultBrand = useDefaultBrand();
    const { handle: defaultBrandHandle = 'urbania' } = defaultBrand || {};
    const [brand = null] = brands || [];
    const { handle: brandHandle = defaultBrandHandle } = brand || {};
    const { slug: documentSlug } = useParams();
    const documentUrl = useDocumentUrlGenerator();
    const intro =
        id !== null
            ? {
                  id: 'intro',
                  type: 'intro',
                  title,
                  blocks,
                  image: headerImage || image,
              }
            : {
                  id: 'intro',
                  type: 'intro',
                  placeholder: true,
              };
    const routes = useRoutes();
    const [, params] = useRoute<{ path?: string }>(routes.survey);
    const { path: routeScreen = null } = params || {};

    useEffect(() => {
        if (routeScreen === null && current && onLeave !== null && !fullscreen && id !== null) {
            onLeave();
        }
    }, [routeScreen, current, onLeave, fullscreen]);

    const onSlideChange = ({ type }) => {
        if ((type === 'intro' || type === 'results') && onLeave !== null && current) {
            onLeave();
        } else if (onEnter !== null && current) {
            onEnter();
        }
    };

    const onAnswer = () => {
        if (onEnter !== null && current) {
            onEnter();
        }
    };

    return (
        <DocumentTheme
            id={`survey-${id}`}
            {...theme}
            containerClassName={styles.container}
            className={classNames([styles.container, className])}
            data-brand={brandHandle}
            data-theme={themeName}
        >
            <Suspense fallback={<div>Loading...</div>}>
                <SurveyComponent
                    title={title}
                    intro={intro}
                    subscriptions={subscriptions}
                    form={form}
                    placeholder={placeholder}
                    className={styles.survey}
                    basePath={documentUrl({
                        type: type || 'survey',
                        slug: slug || documentSlug,
                    })}
                    onSlideChange={onSlideChange}
                    onAnswer={onAnswer}
                />
            </Suspense>
        </DocumentTheme>
    );
}

export default SurveyDocument;
